import React from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useTheme } from '../../../contexts/ThemeContext'

function formatShortDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

export default function ProgressionChart({ points }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)

  if (!points || points.length < 2) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-xs text-muted-foreground">
          {t('workout.chart.notEnoughData')}
        </CardContent>
      </Card>
    )
  }

  const chartData = points.map((p) => ({
    date: formatShortDate(p.date),
    weight: p.weight_kg,
    reps: p.reps,
  }))

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const w = payload[0]?.payload?.weight
    const r = payload[0]?.payload?.reps
    return (
      <div className="bg-gray-900 dark:bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg border border-app-primary-500/30 text-sm">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="font-mono font-bold text-app-primary-400">
          {t('workout.chart.tooltipTop', { weight: Number(w).toFixed(1), reps: r })}
        </p>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-app-primary-500" />
          {t('workout.chart.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
            <AreaChart
              data={chartData}
              margin={isMobile ? { top: 5, right: 5, left: -20, bottom: 0 } : { top: 5, right: 10, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="workoutChartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primary500} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={primary500} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="date"
                interval={isMobile ? 'preserveStartEnd' : 0}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <YAxis
                domain={['dataMin - 2', 'dataMax + 2']}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="weight"
                stroke={primary500}
                strokeWidth={2}
                fill="url(#workoutChartGradient)"
                dot={{ fill: primary500, strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: primary500, stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
